import React from 'react';
import { Button, Row, Col, Tooltip } from 'antd';
import { EyeOutlined, HistoryOutlined, StopOutlined } from '@ant-design/icons';
import {
  CustomModal,
  CustomTable,
  GeneralContainer,
} from '../../../../components';
import { GoodsResponse } from '../../types/goods-response';
import { ShareResponse } from '../../../../share';
import SearchRow from '../components/Filters';
import useApproveRequestsFormik from '../../../../utils/validators/approve-request/approve-request-validator';
import CancelRequest from '../../../ApproveRequests/components/components/ByRoles/CancelRequest';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';

interface WarehouseViewProps {
  data?: GoodsResponse;
  onPagination: (page: number, pageSize: number) => void;
  onViewDetail: (id: string) => void;
  onViewManage: (id: string) => void;
  formik: any;
  states?: ShareResponse[];
  isLoading: boolean;
  onResetFilters: () => void;
  onCancelRequest: (id: string, reason: string, isRefund: boolean) => Promise<void>;
  loadingData?: boolean;
  onViewDetailRefund: (id: string) => void;
  onViewManageRefund: (id: string) => void;
  onViewDetailEntry: (id: string) => void;
}

const WarehouseView: React.FC<WarehouseViewProps> = ({
  data,
  onViewDetail,
  onViewManage,
  onPagination,
  formik,
  states,
  isLoading,
  onResetFilters,
  onCancelRequest,
  loadingData,
  onViewDetailRefund,
  onViewManageRefund,
  onViewDetailEntry,
}) => {
  const [modalCancel, setModalCancel] = React.useState(false);
  const [requestId, setRequestId] = React.useState('');
  const [isRefund, setIsRefund] = React.useState(false);

  const onConfirmModal = () => {
    onCancelRequest(requestId, formikCancelRequest.values.reason, isRefund).finally(
      () => {
        setModalCancel(false);
        setIsRefund(false);
        formikCancelRequest.resetForm();
      }
    );
  };
  const renderActionButton = (record: any) => {
    const ticketType = record?.ticket_type?.replaceAll(' ', '-').toLowerCase();
    if (ticketType === 'solicitud-de-reintegro') {

      return record.can_manage &&
        record.status !== 'Por registrar' &&
        record.status !== 'Rechazado' &&
        record.status !== 'Anulado' &&
        record.status !== 'Finalizado' ? (
        <Tooltip title="Gestionar solicitud">
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={() => onViewManageRefund(record)}
          />
        </Tooltip>
      ) : (
        <>
          <Tooltip title="Ver solicitud">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetailRefund(record)}
            />
          </Tooltip>
          {record.status_id === 12 && (
            <Tooltip title="Anular solicitud">
              <Button
                type="link"
                icon={<StopOutlined />}
                 onClick={() => {
                  setRequestId(record?.id)
                  setIsRefund(true);
                  setModalCancel(true);
                }}
              />
            </Tooltip>
          )}
        </>
      );
    }

    if (ticketType === 'solicitud-de-bienes') {
      return record?.can_manage_warehouse &&
        [9].includes(record?.status_id) ? (
        <Tooltip title="Gestionar solicitud">
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={() => onViewManage(record)}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Ver solicitud">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(record)}
          />
        </Tooltip>
      );
    }

    if (ticketType === 'solicitud-de-entrada') {
      return <Tooltip title="Ver solicitud">
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => onViewDetailEntry(record)}
        />
      </Tooltip>
    }

    return null;
  };

  const columns = [
    {
      title: 'Número de ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Tipo de ticket',
      dataIndex: 'ticket_type',
      key: 'ticket_type',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'formatted_query_date',
      key: 'formatted_query_date',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
      sorter: true,
      align: 'center',
      render: (_: any, item: any) => {
        return `${item?.requester_document} - ${item?.requester}`;
      },
    },
    {
      title: 'Estado de solicitud',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string) => {
        return <CustomAlert status={status} />;
      },
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => (
        <Tooltip title={text || 'Sin registro'}>
          <div
            style={{
              maxWidth: 250,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: '0 auto',
            }}
          >
            {text || 'Sin registro'}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_: any, record: any) => {
        return <>{renderActionButton(record)}</>;
      },
    },
  ];

  const formikCancelRequest = useApproveRequestsFormik({ onSubmit: () => {} });

  return (
    <GeneralContainer title="Vista de almacén" isLoading={loadingData}>
      {/* Filtros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchRow
            formik={formik}
            states={states}
            onCancel={onResetFilters}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <CustomTable
            rowKey="id"
            loading={isLoading}
            columns={columns as any}
            dataSource={
              data?.data?.map((entry) => ({
                ...entry,
                id: Number(entry.id) || -1,
              })) || []
            }
            paginationConfig={{
              current: data?.current_page,
              pageSize: data?.per_page,
              total: data?.total,
              onChange: onPagination,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Col>
      </Row>

      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={modalCancel}
        width={1000}
        onCancel={() => setModalCancel(false)}
        onSave={onConfirmModal}
        disabledBtn={!formikCancelRequest.values.reason}
      >
        <CancelRequest formik={formikCancelRequest} />
      </CustomModal>
    </GeneralContainer>
  );
};

export default WarehouseView;
